import React from 'react';
import PropTypes from 'prop-types';
import className from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { CMS, Config } from '../../utils';
import { TabType, TabTypeProp } from '../../models';
import { Tooltip } from '../../components';
import { isPrimeUserSelector } from '../../redux/userAuthentication/selectors';
import { HSA_SEARCH_FORM_TAB_DESKTOP_CLICK, withClickTrack } from '../../utils/HtmlEvents';

export const Tab = ({ id, onClickTab, isActive, icon, textKey, bubbleKey, tabIds = [] }) => {
    const { t } = useTranslation();
    const bubbleDpText = t(bubbleKey);
    const printBubble = bubbleDpText && !isActive;
    const isPrimeUser = useSelector((state) => isPrimeUserSelector(state));
    const isShowHotelWidget = Config.get('isShowHotelWidget') && isPrimeUser;
    const isShowCarWidget = Config.get('isShowCarWidget') && isPrimeUser;
    const isHotelsOrCarsForPrime = isShowCarWidget || isShowHotelWidget;
    const isTabCarsForPrime = (id === TabType.CARS) && isShowCarWidget;
    const isTabHotelsForPrime = (id === TabType.HOTELS) && isShowHotelWidget;
    const isTabHotelsOrCarsForPrime = isTabCarsForPrime || isTabHotelsForPrime;

    const selectTab = () => {
        onClickTab(id);
    };

    const tabClasses = className({
        'odf-tabs-item': true,
        'active': isActive,
        'odf-tooltip-container open': isTabHotelsOrCarsForPrime || printBubble,
    });

    const iconClasses = className({
        'odf-icon odf-icon-2xl odf-space-outer-right-xs odf-icon-bidi': true,
        [icon]: true,
    });

    const addPrimeTooltips = () => {
        const isOPNL = window.Odigeo?.SessionScope?.website === 'OPNL';
        const tooltipType = isOPNL ? 'prime-light-font-color' : 'prime';
        const isDpTabIncluded = tabIds.includes(TabType.DP);
        let text = null;
        let tracking = null;
        let tooltipPosition = 'left';

        if (isTabHotelsOrCarsForPrime && !isActive) {
            text = t(`prime.home.searchbox.${id}.discount.tooltip`);
            tracking = `product-selector_onload:prime-${id}_pag:home`;
        }

        if (isTabCarsForPrime && !isDpTabIncluded) {
            tooltipPosition = 'right';
        }

        return (
            text ? (
                <Tooltip
                    style={{ marginBottom: 3, marginLeft: 10, borderRadius: 20 }}
                    text={text}
                    position={tooltipPosition}
                    type={tooltipType}
                    tracking={tracking}
                />
            )
                : null);
    };

    const addDynpackTooltip = () => {
        if (printBubble) {
            return (
                <Tooltip
                    text={bubbleDpText}
                    position="center"
                    type="positive"
                    style={{ marginBottom: 'unset' }}
                />
            );
        }
    };

    const TabElem = withClickTrack(({ onClick }) => {
        return (
            <div className={tabClasses} onClick={onClick} test-id={`tab-${id}`}>
                {isHotelsOrCarsForPrime ? addPrimeTooltips() : addDynpackTooltip()}
                <span className={iconClasses} />
                <span><CMS id={textKey} /></span>
            </div>
        );
    });
    const htmlEventID = id === TabType.HOTELS ? HSA_SEARCH_FORM_TAB_DESKTOP_CLICK : null;
    return <TabElem onClick={selectTab} html-event-id={htmlEventID} />;
};

Tab.propTypes = {
    id: TabTypeProp.isRequired,
    icon: PropTypes.string.isRequired,
    onClickTab: PropTypes.func.isRequired,
    textKey: PropTypes.string.isRequired,
    isActive: PropTypes.bool,
    bubbleKey: PropTypes.string,
};

Tab.defaultProps = {
    isActive: false,
    bubbleKey: '',
};
